// Key/mouse binding system.
//
// Actions are named gameplay verbs; each is bound to a keyboard or mouse
// input. The bind map is persisted (keyed off the settings store) and editable
// in the settings menu.

export type Action =
  | "forward"
  | "back"
  | "moveleft"
  | "moveright"
  | "jump"
  | "special"
  | "crouch"
  | "attack"
  | "attack2"
  | "zoom"
  | "ping"
  | "ping_enemy"
  | "ping_heal"
  | "weapon_next"
  | "weapon_prev"
  | "weapon_melee"
  | "weapon_machinegun"
  | "weapon_shotgun"
  | "weapon_grenade"
  | "weapon_rocket"
  | "weapon_plasma"
  | "weapon_lightning"
  | "weapon_railgun"
  | "weapon_experimental"
  | "weapon_ball"
  | "restart"
  | "menu"
  | "position_save"
  | "practice"
  | "noclip"
  | "recall"
  | "recall_prev"
  | "recall_next"
  | "ghost"
  | "wireframe"
  | "profiler"
  | "face_probe"
  | "stuck_probe"
  | "throw_ball"
  | "weeball"
  | "chat"
  | "console"
  | "search"
  | "mark"
  | "screenshot"
  | "vote_yes"
  | "vote_no"
  | "scoreboard"
  | "ready"
  | "spectate_next"
  | "spectate_prev"
  | "spectate_mode";

export interface BindMap {
  [action: string]: string; // input code -> action
}

export const ACTIONS: { id: Action; label: string }[] = [
  { id: "forward", label: "move forward" },
  { id: "back", label: "move back" },
  { id: "moveleft", label: "move left" },
  { id: "moveright", label: "move right" },
  { id: "jump", label: "jump" },
  { id: "special", label: "dash / wall-jump" },
  { id: "crouch", label: "crouch" },
  { id: "attack", label: "attack" },
  { id: "attack2", label: "alt-fire / zoom (combo; rocket = dash script, rail & LG = zoom)" },
  { id: "zoom", label: "zoom (hold)" },
  { id: "ping", label: "ping / tag (team marker)" },
  { id: "ping_enemy", label: "ping: enemies here" },
  { id: "ping_heal", label: "ping: heal here" },
  { id: "weapon_next", label: "next weapon" },
  { id: "weapon_prev", label: "previous weapon" },
  { id: "weapon_melee", label: "weapon: Melee" },
  { id: "weapon_machinegun", label: "weapon: Machinegun" },
  { id: "weapon_shotgun", label: "weapon: Shotgun" },
  { id: "weapon_grenade", label: "weapon: Grenade Launcher" },
  { id: "weapon_rocket", label: "weapon: Rocket Launcher" },
  { id: "weapon_plasma", label: "weapon: Plasma Gun" },
  { id: "weapon_lightning", label: "weapon: Lightning Gun" },
  { id: "weapon_railgun", label: "weapon: Railgun" },
  { id: "weapon_experimental", label: "weapon: Experimental" },
  { id: "weapon_ball", label: "weapon: Ball Launcher" },
  { id: "restart", label: "restart race" },
  { id: "menu", label: "open menu" },
  { id: "position_save", label: "save position" },
  { id: "practice", label: "toggle practice mode" },
  { id: "noclip", label: "toggle noclip" },
  { id: "recall", label: "toggle recall (time travel)" },
  { id: "recall_prev", label: "recall: previous keyframe" },
  { id: "recall_next", label: "recall: next keyframe" },
  { id: "ghost", label: "cycle ghost (last / PR / WR / off)" },
  { id: "vote_yes", label: "vote yes (map vote)" },
  { id: "vote_no", label: "vote no (map vote)" },
  { id: "scoreboard", label: "hold scoreboard" },
  { id: "ready", label: "toggle ready (warmup)" },
  { id: "spectate_next", label: "spectate: next player" },
  { id: "spectate_prev", label: "spectate: previous player" },
  { id: "spectate_mode", label: "spectate: first person / chase" },
  { id: "wireframe", label: "toggle wireframe (debug)" },
  { id: "profiler", label: "toggle profiler (debug)" },
  { id: "face_probe", label: "probe face under crosshair (debug)" },
  { id: "stuck_probe", label: "probe what you're stuck in (debug)" },
  { id: "throw_ball", label: "throw a ball (debug, local practice)" },
  { id: "weeball", label: "throw Healing Weeball (Wipeout)" },
  { id: "chat", label: "chat (temporary)" },
  { id: "console", label: "toggle console" },
  { id: "search", label: "search settings / commands" },
  { id: "mark", label: "mark moment (playtest log)" },
  { id: "screenshot", label: "save a screenshot (F8)" },
];

/** Settings-tab grouping of `ACTIONS` (order matters). Every `Action` must
 * appear in exactly one group here, or its row will never render in
 * Settings → Controls (this is how `weeball` went missing once). */
export const ACTION_GROUPS: { title: string; ids: Action[] }[] = [
  {
    title: "Movement",
    ids: ["forward", "back", "moveleft", "moveright", "jump", "special", "crouch"],
  },
  {
    title: "Combat",
    ids: ["attack", "attack2", "zoom", "ping", "ping_enemy", "ping_heal", "weeball", "weapon_next", "weapon_prev"],
  },
  {
    title: "Weapons",
    ids: [
      "weapon_rocket",
      "weapon_plasma",
      "weapon_grenade",
      "weapon_lightning",
      "weapon_railgun",
      "weapon_shotgun",
      "weapon_machinegun",
      "weapon_melee",
      "weapon_experimental",
      "weapon_ball",
    ],
  },
  { title: "Race", ids: ["restart", "position_save"] },
  {
    title: "Practice & recall",
    ids: ["practice", "noclip", "recall", "recall_prev", "recall_next", "ghost"],
  },
  { title: "Interface", ids: ["menu", "search", "vote_yes", "vote_no", "scoreboard", "ready", "spectate_next", "spectate_prev", "spectate_mode", "chat", "console", "mark", "screenshot"] },
  { title: "Debug", ids: ["wireframe", "profiler", "face_probe", "stuck_probe", "throw_ball"] },
];

/** Action id -> human label (from `ACTIONS`). */
export const ACTION_LABELS: Map<string, string> = new Map(
  ACTIONS.map((a) => [a.id as string, a.label]),
);

/**
 * The actions surfaced in the **Practice** menu tab (recall + practice modes),
 * so their binds live next to the recall UI rather than only in Settings.
 */
export const PRACTICE_ACTIONS: { id: Action; label: string }[] = [
  { id: "restart", label: "restart (start run from keyframe)" },
  { id: "position_save", label: "save position" },
  { id: "practice", label: "toggle practice mode" },
  { id: "noclip", label: "toggle noclip" },
  { id: "recall", label: "toggle recall" },
  { id: "recall_prev", label: "previous keyframe" },
  { id: "recall_next", label: "next keyframe" },
  { id: "ghost", label: "cycle ghost (last / PR / WR / off)" },
];

/**
 * The essential, pre-game bind subset shown on the landing page. These are
 * editable right there; everything else lives in Settings → Controls.
 */
export const ESSENTIAL_ACTION_GROUPS: { title: string; ids: Action[] }[] = [
  {
    title: "Movement",
    ids: ["forward", "back", "moveleft", "moveright", "jump", "special", "crouch"],
  },
  {
    title: "Combat & race",
    ids: [
      "attack",
      "attack2",
      "weapon_rocket",
      "weapon_lightning",
      "weapon_railgun",
      "weapon_shotgun",
      "restart",
      "position_save",
    ],
  },
];


export const DEFAULT_BINDS: BindMap = {
  // Movement.
  KeyW: "forward",
  ArrowUp: "forward",
  KeyS: "back",
  ArrowDown: "back",
  KeyA: "moveleft",
  ArrowLeft: "moveleft",
  KeyD: "moveright",
  ArrowRight: "moveright",
  Mouse2: "jump",
  Space: "special",
  ShiftLeft: "crouch",
  // Combat.
  Mouse0: "attack",
  Mouse4: "attack2",
  // Weapons (next/previous + machinegun/ball are deliberately unbound; use the
  // direct weapon keys).
  KeyF: "weapon_rocket",
  KeyX: "weapon_plasma",
  ControlLeft: "weapon_grenade",
  KeyE: "weapon_lightning",
  KeyR: "weapon_railgun",
  KeyC: "weapon_shotgun",
  Mouse1: "weapon_melee",
  KeyQ: "weapon_experimental",
  // Race.
  Digit4: "restart",
  Mouse3: "position_save",
  // Practice & recall (brackets). Wheel is ping / machinegun by default.
  Digit1: "practice",
  Digit3: "noclip",
  Digit2: "recall",
  BracketLeft: "recall_prev",
  BracketRight: "recall_next",
  WheelUp: "ping",
  WheelDown: "weapon_machinegun",
  KeyH: "ghost",
  // Interface.
  Escape: "menu",
  KeyY: "vote_yes",
  KeyN: "vote_no",
  Tab: "scoreboard",
  F3: "ready",
  KeyP: "spectate_next",
  KeyZ: "spectate_prev",
  KeyV: "spectate_mode",
  Enter: "chat",
  KeyT: "chat",
  KeyM: "search",
  F8: "screenshot",
  F9: "mark",
  Backquote: "console",
  // Extras / debug (not shown in the reference layout).
  KeyG: "weeball",
  KeyL: "wireframe",
  KeyO: "profiler",
  KeyJ: "face_probe",
  KeyK: "stuck_probe",
  KeyB: "throw_ball",
};

/** Mouse button index -> event.button mapping. */
const MOUSE_CODES: Record<string, number> = {
  Mouse0: 0,
  Mouse1: 1,
  Mouse2: 2,
  Mouse3: 3,
  Mouse4: 4,
  Mouse5: 5,
  Mouse6: 6,
};

/** Build a reverse lookup: input code -> action. */
export function codeToAction(binds: BindMap): Map<string, Action> {
  const map = new Map<string, Action>();
  for (const [code, action] of Object.entries(binds)) {
    map.set(code, action as Action);
  }
  return map;
}

/** Return a human-readable name for a bind code. */
export function displayCode(code: string): string {
  if (code.startsWith("Mouse")) {
    // Internal codes are `event.button` (0-based): left 0, middle 1, right 2,
    // back 3, forward 4. Players count conventionally: left 1, right 2,
    // middle 3, back 4, forward 5. Keep the stored codes, fix the labels.
    const n = Number(code.slice("Mouse".length));
    const labels = ["1", "3", "2", "4", "5", "6", "7"];
    return Number.isInteger(n) && labels[n] ? `Mouse ${labels[n]}` : code;
  }
  if (code.startsWith("Key")) return code.slice(3);
  const aliases: Record<string, string> = {
    Space: "Space",
    Escape: "Esc",
    ShiftLeft: "LShift",
    ShiftRight: "RShift",
    ControlLeft: "LCtrl",
    ControlRight: "RCtrl",
    AltLeft: "LAlt",
    AltRight: "RAlt",
    Digit0: "0", Digit1: "1", Digit2: "2", Digit3: "3", Digit4: "4",
    Digit5: "5", Digit6: "6", Digit7: "7", Digit8: "8", Digit9: "9",
    WheelUp: "Wheel ↑",
    WheelDown: "Wheel ↓",
    BracketLeft: "[",
    BracketRight: "]",
  };
  return aliases[code] ?? code;
}

/** Convert a mouse button into its bind code. */
export function mouseButtonToCode(button: number): string {
  for (const [code, b] of Object.entries(MOUSE_CODES)) {
    if (b === button) return code;
  }
  return "";
}



